import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatShortDate } from '../utils/dateUtils';
import './RatingsAdmin.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function RatingsAdmin() {
  const [searchParams] = useSearchParams();
  const playlistFilter = searchParams.get('playlist');

  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [scoreFilter, setScoreFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_BASE}/ratings/admin/recent?limit=500`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setStats(r.data.stats);
        setSubmissions(r.data.submissions || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'Failed to load ratings');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Group submissions by playlist (sorted by most-recent submission within group)
  const grouped = useMemo(() => {
    const list = submissions.filter((s) => {
      if (playlistFilter && String(s.playlistId) !== String(playlistFilter)) return false;
      if (scoreFilter === 'low' && s.overall > 3) return false;
      if (scoreFilter === 'comments' && !(s.comment && s.comment.trim())) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(s.comment || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });

    const map = new Map();
    for (const s of list) {
      if (!map.has(s.playlistId)) {
        map.set(s.playlistId, {
          playlistId: s.playlistId,
          playlistName: s.playlistName,
          playlistDate: s.playlistDate,
          submissions: [],
          totalOverall: 0
        });
      }
      const g = map.get(s.playlistId);
      g.submissions.push(s);
      g.totalOverall += s.overall;
    }
    const groups = Array.from(map.values()).map((g) => ({
      ...g,
      avgOverall: g.submissions.length ? g.totalOverall / g.submissions.length : 0
    }));
    // Most recent group first
    groups.sort((a, b) => {
      const aMax = Math.max(...a.submissions.map((s) => new Date(s.submittedAt).getTime()));
      const bMax = Math.max(...b.submissions.map((s) => new Date(s.submittedAt).getTime()));
      return bMax - aMax;
    });
    return groups;
  }, [submissions, playlistFilter, scoreFilter, search]);

  if (loading) {
    return <div className="ra-page"><p className="ra-info">Loading ratings…</p></div>;
  }
  if (error) {
    return <div className="ra-page"><p className="ra-error">{error}</p></div>;
  }

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', {
        timeZone: 'America/Denver',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    } catch { return ''; }
  };
  const scoreClass = (n) => (n >= 4 ? 'good' : n >= 3 ? 'mid' : 'bad');
  const dimMini = (s) => {
    const fmt = (v) => (v == null ? '—' : v);
    return `${fmt(s.music)} · ${fmt(s.instructing)} · ${fmt(s.other)}`;
  };

  return (
    <div className="ra-page">
      <h1>📋 Class ratings</h1>

      {stats && (
        <div className="ra-stat-row">
          <Stat label="Avg overall (7d)" value={stats.avgOverall7d == null ? '—' : stats.avgOverall7d.toFixed(1)} />
          <Stat label="Submissions (7d)" value={stats.submissions7d} trend={`${stats.classesRated7d} class${stats.classesRated7d === 1 ? '' : 'es'}`} />
          <Stat label="Comment rate" value={`${stats.commentRate7d}%`} />
          <Stat
            label="Worst dimension"
            value={stats.worstDim ? dimLabel(stats.worstDim.name) : '—'}
            trend={stats.worstDim ? `${stats.worstDim.avg.toFixed(1)} avg` : null}
            color="amber"
          />
        </div>
      )}

      <div className="ra-toolbar">
        <div className="ra-filters">
          {playlistFilter && (
            <span className="ra-active-filter">
              Filter: playlist #{playlistFilter}{' '}
              <a href="/admin/ratings">clear ×</a>
            </span>
          )}
          <select value={scoreFilter} onChange={(e) => setScoreFilter(e.target.value)}>
            <option value="all">Any score</option>
            <option value="low">≤ 3 overall (issues)</option>
            <option value="comments">Has a comment</option>
          </select>
          <input
            type="text"
            placeholder="Search comments…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {grouped.length === 0 && (
        <div className="ra-empty">No submissions match this filter.</div>
      )}

      <div className="ra-table-wrap">
        <table className="ra-table">
          <thead>
            <tr>
              <th style={{ width: '110px' }}>Time</th>
              <th style={{ width: '70px' }}>Overall</th>
              <th style={{ width: '180px' }}>Music · Instr · Other</th>
              <th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((g) => (
              <React.Fragment key={g.playlistId}>
                <tr className="ra-group-header">
                  <td colSpan="4">
                    <a href={`/playlist/${g.playlistId}`}>
                      {g.playlistName || formatShortDate(g.playlistDate)}
                    </a>
                    {' · '}
                    {g.submissions.length} submission{g.submissions.length === 1 ? '' : 's'}
                    {' · avg '}
                    {g.avgOverall.toFixed(1)}
                  </td>
                </tr>
                {g.submissions.map((s) => (
                  <tr key={s.id}>
                    <td className="ra-muted">{fmtTime(s.submittedAt)}</td>
                    <td className={`ra-score ${scoreClass(s.overall)}`}>{s.overall}</td>
                    <td className="ra-dim-mini">{dimMini(s)}</td>
                    <td className={s.comment ? 'ra-comment' : 'ra-muted ra-italic'}>
                      {s.comment ? `"${s.comment}"` : 'no comment'}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, trend, color }) {
  return (
    <div className="ra-stat">
      <div className="ra-stat-label">{label}</div>
      <div className={`ra-stat-val ${color || ''}`}>{value}</div>
      {trend && <div className="ra-stat-trend">{trend}</div>}
    </div>
  );
}

function dimLabel(raw) {
  if (raw === 'other_score') return 'Other';
  if (raw === 'instructing') return 'Instructing';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export default RatingsAdmin;
